import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { invoiceSyncApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import { RefreshCw, AlertTriangle, Building2, DollarSign, FileText, Ban, CheckCircle } from 'lucide-react';

function formatCurrency(amount, currency = 'DKK') {
    if (amount == null) return '-';
    return new Intl.NumberFormat('da-DK', { style: 'currency', currency }).format(amount);
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('da-DK', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function AdminInvoiceFinancePage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    // "overdue" comes from Needs Attention → 1 customer with overdue
    // balance. Filters the table below to just companies that actually
    // have an overdue balance, and dismissable via the chip.
    const overdueOnly = searchParams.get('filter') === 'overdue';
    const [overview, setOverview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);
    const [creditDialog, setCreditDialog] = useState({ open: false, org: null });
    const [creditValue, setCreditValue] = useState('');

    const fetchOverview = useCallback(async () => {
        try {
            const { data } = await invoiceSyncApi.getFinanceOverview();
            setOverview(data);
        } catch (err) {
            toast.error('Failed to load finance overview');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchOverview(); }, [fetchOverview]);

    const handleSyncAll = async () => {
        setSyncing(true);
        try {
            const { data } = await invoiceSyncApi.triggerSyncAll();
            const successes = data.results?.filter(r => r.status === 'success').length || 0;
            toast.success(`Synced invoices for ${successes} of ${data.total_organizations} organizations`);
            // Await the overview refresh BEFORE broadcasting so the Header
            // and this page both see the same DB state — otherwise the
            // event races the local fetch and one of them wins with stale
            // data. Two dispatches (immediate + 500 ms delayed) guard
            // against MongoDB read-your-writes lag on production replicas.
            await fetchOverview();
            window.dispatchEvent(new Event('actions-summary-refresh'));
            setTimeout(() => window.dispatchEvent(new Event('actions-summary-refresh')), 500);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Sync failed');
            // Still fire the refresh — a partial sync may have updated
            // some orgs before the error, so the badge can still be
            // stale. Never worse than a no-op.
            window.dispatchEvent(new Event('actions-summary-refresh'));
        } finally {
            setSyncing(false);
        }
    };

    const handleSyncOrg = async (orgId, orgName) => {
        try {
            toast.info(`Syncing ${orgName}...`);
            await invoiceSyncApi.triggerSync(orgId);
            toast.success(`${orgName} synced successfully`);
            await fetchOverview();
            window.dispatchEvent(new Event('actions-summary-refresh'));
            setTimeout(() => window.dispatchEvent(new Event('actions-summary-refresh')), 500);
        } catch (err) {
            toast.error(`Failed to sync ${orgName}`);
            window.dispatchEvent(new Event('actions-summary-refresh'));
        }
    };

    const openCreditDialog = (org) => {
        setCreditDialog({ open: true, org });
        setCreditValue(org.credit_limit != null ? String(org.credit_limit) : '');
    };

    const handleSaveCreditLimit = async () => {
        const org = creditDialog.org;
        if (!org) return;
        try {
            const limit = parseFloat(creditValue);
            if (isNaN(limit) || limit < 0) {
                toast.error('Enter a valid positive amount');
                return;
            }
            await invoiceSyncApi.setCreditLimit(org.id, limit);
            toast.success(`Credit limit set to ${formatCurrency(limit, org.currency || 'DKK')} for ${org.name}`);
            setCreditDialog({ open: false, org: null });
            fetchOverview();
        } catch (err) {
            toast.error('Failed to update credit limit');
        }
    };

    const handleRemoveCreditLimit = async () => {
        const org = creditDialog.org;
        if (!org) return;
        try {
            await invoiceSyncApi.removeCreditLimit(org.id);
            toast.success(`Credit limit removed for ${org.name}`);
            setCreditDialog({ open: false, org: null });
            fetchOverview();
        } catch (err) {
            toast.error('Failed to remove credit limit');
        }
    };

    if (loading) {
        return <div className="max-w-6xl mx-auto p-6 text-center text-muted-foreground">Loading finance overview...</div>;
    }

    return (
        <div className="max-w-6xl mx-auto p-6 space-y-6" data-testid="admin-invoice-finance-page">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Invoices & Credit Overview</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        e-conomic invoice status and credit limits across all companies
                    </p>
                </div>
                <Button
                    onClick={handleSyncAll}
                    disabled={syncing}
                    data-testid="sync-all-btn"
                >
                    <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                    {syncing ? 'Syncing...' : 'Sync All Companies'}
                </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <Card data-testid="total-outstanding-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Total Outstanding</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold">{formatCurrency(overview?.total_outstanding)}</p>
                    </CardContent>
                </Card>
                <Card data-testid="total-overdue-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Total Overdue</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className={`text-2xl font-bold ${overview?.total_overdue > 0 ? 'text-red-600' : ''}`}>
                            {formatCurrency(overview?.total_overdue)}
                        </p>
                        {overview?.oldest_overdue_days > 0 && (
                            <p className="text-xs text-red-500 mt-1" data-testid="admin-oldest-overdue">
                                Oldest: {overview.oldest_overdue_days} days
                            </p>
                        )}
                    </CardContent>
                </Card>
                <Card data-testid="total-companies-card">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Companies in e-conomic</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold">{overview?.total_organizations || 0}</p>
                    </CardContent>
                </Card>
                <Card data-testid="blocked-companies-card" className={overview?.blocked_count > 0 ? 'border-red-300' : ''}>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-muted-foreground">Blocked Companies</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className={`text-2xl font-bold ${overview?.blocked_count > 0 ? 'text-red-600' : 'text-emerald-600'}`}>
                            {overview?.blocked_count || 0}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Duplicate organization records flag — surfaces legacy DB duplicates so admin can clean them up */}
            {overview?.duplicate_organization_groups?.length > 0 && (
                <Card className="border-amber-300 bg-amber-50" data-testid="duplicate-orgs-alert">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-amber-900 flex items-center gap-2">
                            <AlertTriangle className="h-4 w-4" />
                            {overview.duplicate_organization_groups.length} duplicate company name {overview.duplicate_organization_groups.length === 1 ? 'group' : 'groups'} detected
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <p className="text-xs text-amber-800">
                            Multiple organization records share the same normalised name. They are NOT shown as duplicate rows in this report — but you should still merge or delete the redundant entries to keep the data clean.
                        </p>
                        <div className="space-y-2">
                            {overview.duplicate_organization_groups.slice(0, 10).map((group) => (
                                <div key={group.normalized_name} className="text-xs bg-white/60 border border-amber-200 rounded px-3 py-2">
                                    <div className="font-medium text-amber-900 mb-1">{group.records[0]?.name || group.normalized_name}</div>
                                    <ul className="space-y-0.5 text-amber-800">
                                        {group.records.map((r) => (
                                            <li key={r.id} className="flex items-center justify-between gap-2">
                                                <span className="truncate">
                                                    <span className="font-mono text-[10px] opacity-60">{r.id.slice(0, 8)}…</span>
                                                    <span className="ml-2">{r.name}</span>
                                                </span>
                                                <span className="text-[10px] opacity-70 whitespace-nowrap">
                                                    {r.economic_customer_number ? `e-conomic #${r.economic_customer_number}` : 'no e-conomic link'}
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                            {overview.duplicate_organization_groups.length > 10 && (
                                <p className="text-xs text-amber-700 italic">
                                    ...and {overview.duplicate_organization_groups.length - 10} more groups
                                </p>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Blocked Companies Alert */}
            {overview?.blocked_companies?.length > 0 && (
                <Card className="border-red-300 bg-red-50" data-testid="blocked-companies-alert">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-red-800 flex items-center gap-2">
                            <Ban className="h-4 w-4" />
                            Companies exceeding credit limit
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                        {overview.blocked_companies.map((c) => (
                            <div key={c.org_id} className="flex items-center justify-between text-sm">
                                <span className="font-medium text-red-800">{c.name}</span>
                                <span className="text-red-600">
                                    Outstanding: {formatCurrency(c.outstanding, c.currency)} / Limit: {formatCurrency(c.credit_limit, c.currency)}
                                </span>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            {/* Company Table */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <CardTitle className="text-base font-medium">Companies with Outstanding Balance</CardTitle>
                        {overdueOnly && (
                            <div
                                className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-md px-3 py-1 text-sm"
                                data-testid="companies-overdue-filter-chip"
                            >
                                <span className="text-amber-800 font-medium">Filtered: Overdue only</span>
                                <button
                                    type="button"
                                    onClick={() => {
                                        const next = new URLSearchParams(searchParams);
                                        next.delete('filter');
                                        setSearchParams(next, { replace: true });
                                    }}
                                    className="text-amber-600 hover:text-amber-800 font-semibold"
                                    aria-label="Clear filter"
                                    data-testid="companies-overdue-filter-clear"
                                >
                                    ×
                                </button>
                            </div>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full" data-testid="companies-finance-table">
                            <thead>
                                <tr className="border-b bg-muted/50">
                                    <th className="text-left p-3 text-xs font-medium text-muted-foreground uppercase">Company</th>
                                    <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase">Outstanding</th>
                                    <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase">Overdue</th>
                                    <th className="text-right p-3 text-xs font-medium text-muted-foreground uppercase">Credit Limit</th>
                                    <th className="text-center p-3 text-xs font-medium text-muted-foreground uppercase">Status</th>
                                    <th className="text-center p-3 text-xs font-medium text-muted-foreground uppercase">Last Sync</th>
                                    <th className="text-center p-3 text-xs font-medium text-muted-foreground uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(overview?.organizations || [])
                                    .filter((org) => {
                                        // Base filter — never show companies with a
                                        // fully settled balance in this table.
                                        const hasBalance = (org.outstanding_balance || 0) > 0 || (org.overdue_balance || 0) > 0;
                                        if (!hasBalance) return false;
                                        // Needs Attention drill-down — narrow further to
                                        // companies that are actually overdue.
                                        if (overdueOnly) return (org.overdue_balance || 0) > 0;
                                        return true;
                                    })
                                    .map((org) => {
                                    const outstanding = org.outstanding_balance || 0;
                                    const overdue = org.overdue_balance || 0;
                                    const limit = org.credit_limit;
                                    const isBlocked = limit != null && limit > 0 && outstanding > limit;
                                    return (
                                        <tr
                                            key={org.id}
                                            className="border-b last:border-0 hover:bg-muted/30 cursor-pointer"
                                            data-testid={`company-row-${org.id}`}
                                            onClick={() => navigate(`/admin/companies/${org.id}?tab=invoices`)}
                                            // Keyboard parity — row reads as a link to screen readers and
                                            // can be activated with Enter/Space.
                                            tabIndex={0}
                                            role="link"
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    navigate(`/admin/companies/${org.id}?tab=invoices`);
                                                }
                                            }}
                                        >
                                            <td className="p-3">
                                                <div className="flex items-center gap-2">
                                                    <Building2 className="h-4 w-4 text-muted-foreground" />
                                                    <span className="font-medium">{org.name}</span>
                                                </div>
                                            </td>
                                            <td className="p-3 text-right font-medium">
                                                {outstanding > 0 ? formatCurrency(outstanding, org.currency) : '-'}
                                            </td>
                                            <td className="p-3 text-right">
                                                {overdue > 0 ? (
                                                    <div>
                                                        <span className="text-red-600 font-medium">{formatCurrency(overdue, org.currency)}</span>
                                                        {org.oldest_overdue_days > 0 && (
                                                            <span className="block text-[10px] text-red-400">{org.oldest_overdue_days}d overdue</span>
                                                        )}
                                                    </div>
                                                ) : '-'}
                                            </td>
                                            <td className="p-3 text-right">
                                                {limit != null ? formatCurrency(limit, org.currency) : (
                                                    <span className="text-muted-foreground text-xs">Not set</span>
                                                )}
                                            </td>
                                            <td className="p-3 text-center">
                                                {isBlocked ? (
                                                    <Badge className="bg-red-50 text-red-700 border border-red-200 gap-1">
                                                        <Ban className="h-3 w-3" /> Blocked
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 gap-1">
                                                        <CheckCircle className="h-3 w-3" /> OK
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="p-3 text-center text-xs text-muted-foreground">
                                                {formatDate(org.invoice_sync_last_at)}
                                            </td>
                                            <td className="p-3 text-center" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center justify-center gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => handleSyncOrg(org.id, org.name)} data-testid={`sync-btn-${org.id}`}>
                                                        <RefreshCw className="h-3.5 w-3.5" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" onClick={() => openCreditDialog(org)} data-testid={`credit-btn-${org.id}`}>
                                                        <DollarSign className="h-3.5 w-3.5" />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        {(overview?.organizations || []).filter((org) => (org.outstanding_balance || 0) > 0 || (org.overdue_balance || 0) > 0).length === 0 && (
                            <div className="py-10 text-center text-sm text-muted-foreground">
                                <CheckCircle className="h-8 w-8 mx-auto mb-2 text-emerald-500" />
                                No companies have outstanding balances right now.
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Credit Limit Dialog */}
            <Dialog open={creditDialog.open} onOpenChange={(open) => setCreditDialog({ ...creditDialog, open })}>
                <DialogContent data-testid="credit-limit-dialog">
                    <DialogHeader>
                        <DialogTitle>Set Credit Limit — {creditDialog.org?.name}</DialogTitle>
                        <DialogDescription>
                            Set the maximum outstanding invoice amount. Orders will be blocked when exceeded.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <label className="text-sm font-medium mb-2 block">
                                Credit Limit ({creditDialog.org?.currency || 'DKK'})
                            </label>
                            <Input
                                type="number"
                                min="0"
                                step="1000"
                                value={creditValue}
                                onChange={(e) => setCreditValue(e.target.value)}
                                placeholder="e.g. 50000"
                                data-testid="credit-limit-input"
                            />
                        </div>
                        {creditDialog.org && (
                            <div className="text-sm text-muted-foreground space-y-1">
                                <p>Current outstanding: <span className="font-medium">{formatCurrency(creditDialog.org.outstanding_balance, creditDialog.org.currency)}</span></p>
                                <p>Current limit: <span className="font-medium">{creditDialog.org.credit_limit != null ? formatCurrency(creditDialog.org.credit_limit, creditDialog.org.currency) : 'None'}</span></p>
                            </div>
                        )}
                    </div>
                    <DialogFooter className="flex gap-2">
                        {creditDialog.org?.credit_limit != null && (
                            <Button variant="outline" onClick={handleRemoveCreditLimit} data-testid="remove-credit-limit-btn">
                                Remove Limit
                            </Button>
                        )}
                        <Button onClick={handleSaveCreditLimit} data-testid="save-credit-limit-btn">
                            Save Credit Limit
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
